import { useState, useMemo } from "react";

export interface Supervision {
  id: number;
  aia_supervision_id: string;
  aia_game_id: string;
  aia_game_name: string;
  aia_supervision_date: string;
  aia_supervisor_id: string;
  // Progress in AIA: "to_do" | "done"
  aia_supervision_status: string;
  invoice_status?: string;
  supervisorId: number | null;
  gameId: number | null;
  contractId: number | null;
  factureId: number | null;
  supervisor?: {
    first_name: string;
    last_name: string;
    email: string;
  };
  game?: {
    gameName: string;
  };
  contract?: {
    status: string;
  };
  facture?: {
    payment_date: string;
    payment_ref: string;
  };
  metadata: any;
  game_metadata?: {
    status?: string;
    // How the game is coded, from AIA's `games.coding_method`. A supervision covers a
    // whole game, so the method belongs to the game rather than to the supervision.
    coding_method?: string | null;
    [key: string]: any;
  };
}

export interface SupervisionFilters {
  dateFrom: string;
  dateTo: string;
  supervisor: string;
  status: string;
  /** "true" to keep only supervisions whose match is imminent and that have no supervisor. */
  lateAssignment: string;
  sortBy: "date" | "supervisor" | "supervisionStatus" | "";
  sortOrder: "asc" | "desc";
}

export const useSupervisionFilters = (
  supervisions: Supervision[],
  initialFilters?: Partial<SupervisionFilters>,
) => {
  const [filters, setFilters] = useState<SupervisionFilters>({
    dateFrom: "",
    dateTo: "",
    supervisor: "",
    status: "",
    lateAssignment: "",
    sortBy: "date",
    sortOrder: "desc",
    ...initialFilters,
  });

  // Generate filter options from the data
  // Filter and sort the supervisions
  /**
   * Only sorts. Every filter is sent to the API by the page, which matches the
   * whole table — matching them again here would be redundant, and matching them
   * *only* here would restrict the result to whichever rows the current page
   * happens to hold.
   */
  /**
   * Passthrough. Filtering and sorting are both the API's job now: sorting here
   * reordered only the rows of the current page, so clicking a header rearranged 25
   * of several dozen supervisions as though the whole table had been sorted.
   */
  const filteredSupervisions = useMemo(() => supervisions, [supervisions]);

  const updateFilter = (key: keyof SupervisionFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updateSort = (sortBy: SupervisionFilters["sortBy"]) => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder:
        prev.sortBy === sortBy && prev.sortOrder === "asc" ? "desc" : "asc",
    }));
  };

  const resetFilters = () => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      supervisor: "",
      status: "",
      lateAssignment: "",
      sortBy: "date",
      sortOrder: "desc",
    });
  };

  const hasActiveFilters = useMemo(() => {
    return (
      filters.dateFrom ||
      filters.dateTo ||
      filters.supervisor ||
      filters.status ||
      filters.lateAssignment
    );
  }, [filters]);

  return {
    filters,
    filteredSupervisions,
    updateFilter,
    updateSort,
    resetFilters,
    hasActiveFilters,
  };
};
